import { Entity } from "game-common/models"
import { logger } from "../../../logger"

export interface PerformanceMetrics {
    frameRate: number
    entityCount: number
    renderedEntities: number
    culledEntities: number
    viewportCullingStats: any
    spritePoolStats: any
    renderTime: number
    updateTime: number
    totalTime: number
    memoryUsage?: number
}

export interface PerformanceConfig {
    enableMetrics: boolean
    logInterval: number // milliseconds
    enableMemoryTracking: boolean
    enableDetailedStats: boolean
}

export class PerformanceMonitor {
    private config: PerformanceConfig
    private frameCount: number = 0
    private lastFrameTime: number = 0
    private frameTimes: number[] = []
    private renderTimes: number[] = []
    private updateTimes: number[] = []
    private lastLogTime: number = 0
    private entityCount: number = 0
    private renderedEntityCount: number = 0
    private culledEntityCount: number = 0
    private startTime: number = 0
    private renderStartTime: number = 0
    private updateStartTime: number = 0

    // Callbacks for getting stats from other systems
    private getViewportCullingStats?: () => any
    private getSpritePoolStats?: () => any

    constructor(config?: Partial<PerformanceConfig>) {
        this.config = {
            enableMetrics: true,
            logInterval: 5000, // 5 seconds
            enableMemoryTracking: false,
            enableDetailedStats: false,
            ...config,
        }
    }

    /**
     * Set callbacks to get stats from other systems
     */
    setStatCallbacks(callbacks: {
        getViewportCullingStats?: () => any
        getLODStats?: () => any
        getSpritePoolStats?: () => any
        getLODDistribution?: () => Record<string, number>
    }) {
        this.getViewportCullingStats = callbacks.getViewportCullingStats
        this.getSpritePoolStats = callbacks.getSpritePoolStats
    }

    /**
     * Start timing a frame
     */
    startFrame(): void {
        this.startTime = performance.now()
        this.frameCount++
    }

    /**
     * Start timing render phase
     */
    startRender(): void {
        this.renderStartTime = performance.now()
    }

    /**
     * End timing render phase
     */
    endRender(): void {
        if (this.renderStartTime > 0) {
            const renderTime = performance.now() - this.renderStartTime
            this.renderTimes.push(renderTime)

            // Keep only last 60 frames
            if (this.renderTimes.length > 60) {
                this.renderTimes.shift()
            }
        }
    }

    /**
     * Start timing update phase
     */
    startUpdate(): void {
        this.updateStartTime = performance.now()
    }

    /**
     * End timing update phase
     */
    endUpdate(): void {
        if (this.updateStartTime > 0) {
            const updateTime = performance.now() - this.updateStartTime
            this.updateTimes.push(updateTime)

            // Keep only last 60 frames
            if (this.updateTimes.length > 60) {
                this.updateTimes.shift()
            }
        }
    }

    /**
     * End timing a frame
     */
    endFrame(): void {
        const currentTime = performance.now()
        const frameTime = currentTime - this.startTime

        // Clamp frame time to reasonable values (avoid division by very small numbers)
        const clampedFrameTime = Math.max(frameTime, 0.1) // Minimum 0.1ms
        this.frameTimes.push(clampedFrameTime)

        // Keep only last 60 frames for rolling average
        if (this.frameTimes.length > 60) {
            this.frameTimes.shift()
        }

        this.lastFrameTime = frameTime

        // Log metrics periodically
        if (this.config.enableMetrics && currentTime - this.lastLogTime > this.config.logInterval) {
            this.logMetrics()
            this.lastLogTime = currentTime
        }
    }

    /**
     * Update entity counts
     */
    updateEntityCounts(total: number, rendered: number, culled: number): void {
        this.entityCount = total
        this.renderedEntityCount = rendered
        this.culledEntityCount = culled
    }

    /**
     * Get current performance metrics
     */
    getMetrics(): PerformanceMetrics {
        const avgFrameTime =
            this.frameTimes.length > 0 ? this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length : 0

        const avgRenderTime =
            this.renderTimes.length > 0 ? this.renderTimes.reduce((a, b) => a + b, 0) / this.renderTimes.length : 0

        const avgUpdateTime =
            this.updateTimes.length > 0 ? this.updateTimes.reduce((a, b) => a + b, 0) / this.updateTimes.length : 0

        const frameRate = avgFrameTime > 0 ? 1000 / avgFrameTime : 0

        return {
            frameRate: Math.round(frameRate * 10) / 10,
            entityCount: this.entityCount,
            renderedEntities: this.renderedEntityCount,
            culledEntities: this.culledEntityCount,
            viewportCullingStats: this.getViewportCullingStats?.() || {},
            spritePoolStats: this.getSpritePoolStats?.() || {},
            renderTime: Math.round(avgRenderTime * 100) / 100,
            updateTime: Math.round(avgUpdateTime * 100) / 100,
            totalTime: Math.round(avgFrameTime * 100) / 100,
            memoryUsage: this.config.enableMemoryTracking ? this.getMemoryUsage() : undefined,
        }
    }

    /**
     * Log performance metrics to console
     */
    logMetrics(): void {
        const metrics = this.getMetrics()

        logger.info({ tags: ["performance", "metrics"] }, "=== Performance Metrics ===")
        logger.info({ tags: ["performance", "metrics"] }, `Frame Rate: ${metrics.frameRate} FPS`)
        logger.info(
            { tags: ["performance", "metrics"] },
            `Entities: ${metrics.renderedEntities}/${metrics.entityCount} rendered (${metrics.culledEntities} culled)`,
        )
        logger.info({ tags: ["performance", "metrics"] }, `Render Time: ${metrics.renderTime}ms`)
        logger.info({ tags: ["performance", "metrics"] }, `Update Time: ${metrics.updateTime}ms`)
        logger.info({ tags: ["performance", "metrics"] }, `Total Time: ${metrics.totalTime}ms`)

        if (this.config.enableDetailedStats) {
            logger.debug({ tags: ["performance", "viewport"] }, "Viewport Culling:", metrics.viewportCullingStats)
            logger.debug({ tags: ["performance", "sprites"] }, "Sprite Pool:", metrics.spritePoolStats)

            // Debug: Log if too many entities are being culled
            const cullRate =
                metrics.entityCount > 0 ? ((metrics.culledEntities / metrics.entityCount) * 100).toFixed(1) : "0"
            if (parseFloat(cullRate) > 80) {
                logger.warn(
                    { tags: ["performance", "culling"] },
                    `High cull rate: ${cullRate}% (${metrics.culledEntities}/${metrics.entityCount} entities culled)`,
                )
            }
        }

        if (metrics.memoryUsage !== undefined) {
            logger.info({ tags: ["performance", "memory"] }, `Memory Usage: ${metrics.memoryUsage}MB`)
        }

        logger.info({ tags: ["performance", "metrics"] }, "========================")
    }

    /**
     * Get memory usage (if available)
     */
    private getMemoryUsage(): number | undefined {
        if (typeof performance !== "undefined" && "memory" in performance) {
            const memory = (performance as any).memory
            return Math.round((memory.usedJSHeapSize / 1024 / 1024) * 100) / 100
        }
        return undefined
    }

    /**
     * Reset all metrics
     */
    reset(): void {
        this.frameCount = 0
        this.frameTimes = []
        this.renderTimes = []
        this.updateTimes = []
        this.entityCount = 0
        this.renderedEntityCount = 0
        this.culledEntityCount = 0
        this.lastLogTime = 0
    }

    /**
     * Update configuration
     */
    updateConfig(newConfig: Partial<PerformanceConfig>): void {
        this.config = { ...this.config, ...newConfig }
    }

    /**
     * Get current configuration
     */
    getConfig(): PerformanceConfig {
        return { ...this.config }
    }

    /**
     * Get performance warnings
     */
    getPerformanceWarnings(): string[] {
        const warnings: string[] = []
        const metrics = this.getMetrics()

        if (metrics.frameRate < 30) {
            warnings.push(`Low frame rate: ${metrics.frameRate} FPS`)
        }

        if (metrics.totalTime > 33) {
            warnings.push(`High frame time: ${metrics.totalTime}ms`)
        }

        if (metrics.renderedEntities > 100) {
            warnings.push(`High entity count: ${metrics.renderedEntities} entities`)
        }

        if (metrics.renderTime > 16) {
            warnings.push(`High render time: ${metrics.renderTime}ms`)
        }

        return warnings
    }
}
